/**
 * Which server message deserves which piece of feedback.
 *
 * This is a pure lookup table with its own exhaustiveness guard, deliberately
 * kept out of `applyServerMessage`'s thirteen case arms: woven in there, someone
 * eventually adds a message and forgets the sound. Two switches with two `never`
 * guards means adding a `ServerMessage` breaks the build in both places, which is
 * exactly what should happen.
 *
 * No timing lives here. Delays and spacing belong to the scheduler in
 * `lib/feedback.ts`, which keeps this a table you can assert.
 *
 * NOTE: this is safe against reconnect storms only because the server sends no
 * event replay — `onJoin`/`onReconnect` send `joined` plus a state broadcast and
 * nothing else. If a replay message is ever added to the protocol, it MUST be
 * excluded here, or reconnecting will fire a wall of cues.
 */

import type { ServerMessage } from '@shared/types/websocket';
import type { LiveData } from '@/store/live';

export type FeedbackCue =
  | { kind: 'card-place' }
  | { kind: 'trick-sweep'; mine: boolean }
  | { kind: 'your-turn' }
  | { kind: 'bid-exact' }
  | { kind: 'bid-missed' }
  | { kind: 'refused' }
  | { kind: 'deal'; ticks: number }
  | { kind: 'game-over' }
  | { kind: 'player-joined' };

/** Carries an id so tests and devtools can talk about ordering. */
export interface FeedbackEvent {
  id: number;
  cue: FeedbackCue;
}

/** A busy round should sound busier than a one-card round. */
function dealTicks(cards: number): number {
  return Math.max(2, Math.min(5, cards));
}

export function cueFor(prev: LiveData, message: ServerMessage): FeedbackCue | null {
  switch (message.type) {
    case 'card-played':
      // Every seat, including your own. There is deliberately no local click on
      // tap: one sound, fired when the server echoes the card back, so it means
      // "accepted" — and if the socket is down, the silence is honest.
      return { kind: 'card-place' };

    case 'trick-won':
      return { kind: 'trick-sweep', mine: message.seat === prev.seat };

    case 'round-dealt':
      return { kind: 'deal', ticks: dealTicks(message.cards) };

    case 'round-scored': {
      // Personal, not table-wide: you hear your own result.
      const mine = message.entries.find((entry) => entry.seat === prev.seat);
      if (!mine) return null;
      return mine.bid === mine.tricksWon ? { kind: 'bid-exact' } : { kind: 'bid-missed' };
    }

    case 'game-ended':
      return { kind: 'game-over' };

    case 'error':
      // Every error code gets the thud; it is the "no" sound, and ILLEGAL_CARD
      // and NOT_YOUR_TURN are by far the common ones.
      return { kind: 'refused' };

    case 'player-joined':
      return { kind: 'player-joined' };

    case 'state-update': {
      // The only cue without an event of its own. The transition check is what
      // stops it re-firing on the authoritative snapshot that follows every
      // other event.
      if (!prev.state || prev.seat === null) return null;
      const next = message.state.turnSeat;
      if (next === prev.state.turnSeat) return null;
      return next === prev.seat ? { kind: 'your-turn' } : null;
    }

    // Deliberately silent:
    // - `joined` fires on every reconnect, so flaky wifi would blip repeatedly.
    // - `player-reconnected` is broadcast to the reconnecting client too, so it
    //   would beep at you about yourself.
    // - bids come up to seven in a row, each already followed by someone's
    //   `your-turn`.
    //
    // The hartenjagen and klaverjassen messages below are silent here for a
    // different reason: each of those games narrates itself through its own
    // store, and this hook only serves boerenbridge.
    //
    // All of it in one group, with no comments *between* the labels — a comment
    // there makes eslint's no-fallthrough treat the group as non-empty and
    // report every label after it.
    case 'joined':
    case 'player-left':
    case 'player-reconnected':
    case 'bid-placed':
    case 'all-bids-in':
    case 'hand-dealt':
    case 'cards-exchanged':
    case 'hand-scored':
    case 'deal-dealt':
    case 'trump-chosen':
    case 'trump-passed':
    case 'deal-scored':
    case 'roem-claimed':
      return null;

    default: {
      const exhaustive: never = message;
      void exhaustive;
      return null;
    }
  }
}
